/**
 * Zustand `useJoints` equality: compare joint-like records while skipping selected keys (e.g. `ref`, `range`)
 * so value/limit thumb drags do not rerender snapshots that still read live data from narrower subscriptions.
 */

type JointLikeRecord = Record<string, unknown>;

function structuralEqualSkippingKeys(
  a: JointLikeRecord,
  b: JointLikeRecord,
  ignoreKeys: Set<string>
): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if (ignoreKeys.has(k)) continue;
    const va = a[k];
    const vb = b[k];
    if (va === vb) continue;
    if (Array.isArray(va) && Array.isArray(vb)) {
      if (va.length !== vb.length) return false;
      for (let i = 0; i < va.length; i++) {
        if (!Object.is(va[i], vb[i])) return false;
      }
      continue;
    }
    return false;
  }
  return true;
}

export function jointStructuralEqualIgnoring(
  a: JointLikeRecord | null | undefined,
  b: JointLikeRecord | null | undefined,
  ignoreKeys: Set<string>
): boolean {
  if (Object.is(a, b)) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a !== "object" || typeof b !== "object") return false;
  return structuralEqualSkippingKeys(a, b, ignoreKeys);
}

const REF_ONLY = new Set(["ref"]);
const REF_AND_RANGE = new Set(["ref", "range"]);

/** Equality for selectors where only `ref` changes (hinge/slide drag). */
export function jointStructuralEqualIgnoreRef(
  a: JointLikeRecord | null | undefined,
  b: JointLikeRecord | null | undefined
): boolean {
  return jointStructuralEqualIgnoring(a, b, REF_ONLY);
}

/** Equality for inspectors that subscribe elsewhere for scalar value + limits (ref + range). */
export function jointStructuralEqualIgnoreRefAndRange(
  a: JointLikeRecord | null | undefined,
  b: JointLikeRecord | null | undefined
): boolean {
  return jointStructuralEqualIgnoring(a, b, REF_AND_RANGE);
}
